import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Check, X } from "lucide-react";
import { useSelector } from "react-redux";
import { useAppDispatch, RootState } from "../../../redux/store";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { getSites, getDepartments, searchEmployee } from "../../../redux/features/common/api";
import { EmployeeSearchResult } from "../../../types/response";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { isPrivilege, UserPrivileges } from "../../../rules";

interface FilterState {
    departmentCodes: string[];
    groupCodes: string[];
    siteCodes: string[];
    fields: string[];
    selectedEmployees: EmployeeSearchResult[];
}

// Mock data for groups (temporarily kept for hidden groups section)
const mockGroups = [
    { value: "testgroup", label: "Test Group" },
    { value: "group123", label: "Group 123" },
    { value: "group456", label: "Group 456" }
];

const fieldOptions = [
    { value: "total_hours", label: "Total Hours" },
    { value: "total_overtime", label: "Overtime Hours" },
    { value: "doubletime_hours", label: "Doubletime Hours" },
    { value: "total_break_minutes", label: "Break Minutes" }
];

const ManagerTimesheetFilters: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const isTimeoffMode = location.pathname === "/m-manager-timeoff-filters" || (location.state as any)?.mode === "timeoff";
    const filterStorageKey = isTimeoffMode ? "managerTimeoffFilters" : "managerTimesheetFilters";
    const returnPath = isTimeoffMode ? "/m-manager-timeoff" : "/m-manager-timesheet";
    
    // Get sites and departments from Redux store
    const { sites, departments } = useSelector((state: RootState) => state.common);
    const user = useSelector((state: RootState) => state.user.user);
    
    // Get initial filters from location state or use defaults
    const initialFilters = (location.state as FilterState) || {
        departmentCodes: [],
        groupCodes: [],
        siteCodes: [],
        fields: ["total_hours"],
        selectedEmployees: []
    };
    
    const [filters, setFilters] = useState<FilterState>(initialFilters);
    const [selectedEmployees, setSelectedEmployees] = useState<EmployeeSearchResult[]>(
        initialFilters.selectedEmployees || []
    );
    const [searchInput, setSearchInput] = useState("");
    
    // Format department options for react-select from Redux data
    const departmentOptions = departments.map((dept: any) => ({
        value: dept.department_code || dept.code || '',
        label: dept.department_name || dept.name || ''
    }));
    
    // Format site options for react-select from Redux data
    const siteOptions = sites.map((site: any) => ({
        value: site.us_site_code || '',
        label: site.user_site_name || ''
    }));
    
    const [selectedDepartments, setSelectedDepartments] = useState(
        departmentOptions.filter((dept: any) => filters.departmentCodes.includes(dept.value))
    );
    
    const [selectedGroups, setSelectedGroups] = useState(
        mockGroups.filter((group: any) => filters.groupCodes.includes(group.value))
    );
    
    const [selectedSites, setSelectedSites] = useState(
        siteOptions.filter((site: any) => filters.siteCodes.includes(site.value))
    );
    
    const [selectedFields, setSelectedFields] = useState(
        fieldOptions.filter(field => filters.fields.includes(field.value))
    );

    const employeeSearchPrivilege = useMemo(() => {
        const hasUpdateTimesheetAccess = isPrivilege({
            privileges: user?.employee_access_role_privileges,
            rule: UserPrivileges.USER_PRIV_UPDATE_REPORTING_EMP_TIMESHEETS
        });
        const hasViewTimesheetAccess = isPrivilege({
            privileges: user?.employee_access_role_privileges,
            rule: UserPrivileges.VIEW_TIMESHEET
        });

        if (hasUpdateTimesheetAccess) {
            return "USER_PRIV_UPDATE_REPORTING_EMP_TIMESHEETS";
        }

        if (hasViewTimesheetAccess) {
            return "view-timesheet";
        }

        return undefined;
    }, [user?.employee_access_role_privileges]);

    // Employee search function for AsyncSelect
    const loadEmployeeOptions = useCallback(async (inputValue: string) => {
        if (inputValue.length < 3) {
            return [];
        }
        
        try {
            const response = await dispatch(searchEmployee({
                search: inputValue,
                departments: filters.departmentCodes.join(','),
                groups: filters.groupCodes.join(','),
                site_id: filters.siteCodes.join(','),
                page: 1,
                emp_status: 'all', // Only search all employees
                priv: employeeSearchPrivilege
            })).unwrap();
            
            // Filter out already selected employees
            const selectedIds = selectedEmployees.map(emp => emp.os_emp_account_id);
            return response.data.employees
                .filter((employee: EmployeeSearchResult) => !selectedIds.includes(employee.os_emp_account_id))
                .map((employee: EmployeeSearchResult) => ({
                    value: employee,
                    label: employee.text
                }));
        } catch (error) {
            console.error('Error searching employees:', error);
            return [];
        }
    }, [dispatch, employeeSearchPrivilege, filters.departmentCodes, filters.groupCodes, filters.siteCodes, selectedEmployees]);

    useEffect(() => {
        dispatch(setTopNavNameTitle(isTimeoffMode ? "Time Off Filters" : "Timesheet Filters"));
        // Fetch sites and departments data
        dispatch(getSites());
        dispatch(getDepartments());
    }, [dispatch, isTimeoffMode]);

    const handleDepartmentChange = (selected: any) => {
        setSelectedDepartments(selected || []);
        setFilters(prev => ({
            ...prev,
            departmentCodes: (selected || []).map((item: any) => item.value)
        }));
    };
    
    const handleGroupChange = (selected: any) => {
        setSelectedGroups(selected || []);
        setFilters(prev => ({
            ...prev,
            groupCodes: (selected || []).map((item: any) => item.value)
        }));
    };
    
    const handleSiteChange = (selected: any) => {
        setSelectedSites(selected || []);
        setFilters(prev => ({
            ...prev,
            siteCodes: (selected || []).map((item: any) => item.value)
        }));
    };
    
    const handleFieldChange = (selected: any) => {
        setSelectedFields(selected || []);
        setFilters(prev => ({
            ...prev,
            fields: (selected || []).map((item: any) => item.value)
        }));
    };
    
    const handleEmployeeSelect = (selected: any) => {
        if (selected?.value) {
            const newEmployee = selected.value;
            const updatedEmployees = [...selectedEmployees, newEmployee];
            setSelectedEmployees(updatedEmployees);
            setFilters(prev => ({
                ...prev,
                selectedEmployees: updatedEmployees
            }));
            setSearchInput(""); // Clear search input after selection
        }
    };

    const handleRemoveEmployee = (employeeId: string) => {
        const updatedEmployees = selectedEmployees.filter(emp => emp.os_emp_account_id !== employeeId);
        setSelectedEmployees(updatedEmployees);
        setFilters(prev => ({
            ...prev,
            selectedEmployees: updatedEmployees
        }));
    };

    const handleApplyFilters = () => {
        // Store filters in localStorage for persistence
        localStorage.setItem(filterStorageKey, JSON.stringify(filters));
        
        // Navigate back to employee view with updated filters
        navigate(returnPath, { state: filters });
    };
    
    const handleClearFilters = () => {
        const emptyFilters = {
            departmentCodes: [],
            groupCodes: [],
            siteCodes: [],
            fields: ["total_hours"],
            selectedEmployees: []
        };
        
        setFilters(emptyFilters);
        setSelectedDepartments([]);
        setSelectedGroups([]);
        setSelectedSites([]);
        setSelectedFields(fieldOptions.filter(field => field.value === "total_hours"));
        setSelectedEmployees([]);
        setSearchInput("");
        
        localStorage.removeItem(filterStorageKey);
    };
    
    const handleCancel = () => {
        navigate(returnPath);
    };

    return (
        <div className="container mx-auto bg-white h-full">
            <div className="flex flex-col text-gray-800 text-base font-sans h-full">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <button 
                        onClick={handleCancel}
                        className="flex items-center text-blue-500"
                    >
                        <X size={18} />
                        <span className="ml-1">Cancel</span>
                    </button>
                    
                    <div className="font-medium">Filter Options</div>
                    
                    <button
                        onClick={handleApplyFilters}
                        className="flex items-center text-blue-500"
                    >
                        <Check size={18} />
                        <span className="ml-1">Apply</span>
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm text-gray-700 font-medium">Employees</label>
                            {selectedEmployees.length > 0 && (
                                <button 
                                    className="text-xs text-blue-500"
                                    onClick={() => {
                                        setSelectedEmployees([]);
                                        setFilters(prev => ({ ...prev, selectedEmployees: [] }));
                                    }}
                                >
                                    Clear All
                                </button>
                            )}
                        </div>
                        
                        {/* Selected employees as chips */}
                        {selectedEmployees.length > 0 && (
                            <div className="mb-3 flex flex-wrap gap-2">
                                {selectedEmployees.map((employee) => (
                                    <div
                                        key={employee.os_emp_account_id}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                    >
                                        <span>{employee.text}</span>
                                        <button
                                            onClick={() => handleRemoveEmployee(employee.os_emp_account_id)}
                                            className="ml-2 text-blue-600 hover:text-blue-800"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        <AsyncSelect
                            value={null}
                            onChange={handleEmployeeSelect}
                            loadOptions={loadEmployeeOptions}
                            placeholder="Search employees (min 3 characters)"
                            noOptionsMessage={({ inputValue }) => 
                                inputValue.length < 3 ? "Type at least 3 characters to search" : "No employees found"
                            }
                            className="text-sm"
                            classNamePrefix="react-select"
                            isClearable
                            isSearchable
                            inputValue={searchInput}
                            onInputChange={setSearchInput}
                        />
                    </div>
                    
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm text-gray-700 font-medium">Departments</label>
                            <button 
                                className="text-xs text-blue-500"
                                onClick={() => setSelectedDepartments([])}
                            >
                                Clear
                            </button>
                        </div>
                        
                        <Select 
                            isMulti
                            value={selectedDepartments}
                            onChange={handleDepartmentChange}
                            options={departmentOptions}
                            className="text-sm"
                            classNamePrefix="react-select"
                            placeholder="Select Departments"
                        />
                    </div>
                    
                    {/* Temporarily hidden - Groups section */}
                    {/* <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm text-gray-700 font-medium">Groups</label>
                            <button 
                                className="text-xs text-blue-500"
                                onClick={() => setSelectedGroups([])}
                            >
                                Clear
                            </button>
                        </div>
                        
                        <Select
                            isMulti
                            value={selectedGroups}
                            onChange={handleGroupChange}
                            options={mockGroups}
                            className="text-sm"
                            classNamePrefix="react-select"
                            placeholder="Select Groups"
                        />
                    </div> */}
                    
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm text-gray-700 font-medium">Sites</label>
                            <button 
                                className="text-xs text-blue-500"
                                onClick={() => setSelectedSites([])}
                            >
                                Clear
                            </button>
                        </div>
                        
                        <Select
                            isMulti
                            value={selectedSites}
                            onChange={handleSiteChange}
                            options={siteOptions}
                            className="text-sm"
                            classNamePrefix="react-select"
                            placeholder="Select Sites"
                        />
                    </div>
                    
                    {/* <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm text-gray-700 font-medium">Data Fields</label>
                            <button 
                                className="text-xs text-blue-500"
                                onClick={() => setSelectedFields([fieldOptions[0]])}
                            >
                                Reset
                            </button>
                        </div>
                        
                        <Select
                            isMulti
                            value={selectedFields}
                            onChange={handleFieldChange}
                            options={fieldOptions}
                            className="text-sm"
                            classNamePrefix="react-select"
                            placeholder="Select Data Fields"
                        />
                    </div> */}
                    
                    <div className="my-6">
                        <button
                            onClick={handleClearFilters}
                            className="w-full py-3 text-center border border-gray-300 rounded-md text-gray-700 font-medium"
                        >
                            Clear All Filters
                        </button>
                    </div>
                    
                    {/* <div className="mb-6">
                        <button
                            onClick={() => {
                                // Save as preset - this would implement saving named presets
                                alert("This would save the current filter set as a preset");
                            }}
                            className="w-full py-3 text-center bg-blue-500 text-white rounded-md font-medium"
                        >
                            Save as Preset
                        </button>
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default ManagerTimesheetFilters;
